<script setup lang="ts">
import { watch, onMounted, onActivated } from "vue";
import { defu } from "defu";
import { getTranslatedProperty } from "@shopware/helpers";
import { useCmsHead } from "@/composables/useCmsHead";
import type { Ref } from "vue";
import { useCategorySearch } from "#imports";
import type { Schemas } from "#shopware";
import { useNavigationTrailStore } from "@/stores/navigationTrail";
import type { Breadcrumb } from "@/composables/useBreadcrumbs";

const props = defineProps<{
  navigationId: string;
}>();

const route = useRoute();
const { search } = useCategorySearch();
const { apiClient } = useShopwareContext();
const { buildDynamicBreadcrumbs } = useBreadcrumbs();
const navigationTrailStore = useNavigationTrailStore();
const errors = ref<string[]>([]);

const { data, error } = await useAsyncData(
  `cmsNavigation${props.navigationId}`,
  async () => {
    const responses = await Promise.allSettled([
      search(props.navigationId, {
        withCmsAssociations: true,
        query: {
          ...route.query,
        },
      }),
      apiClient
        .invoke("readBreadcrumb get /breadcrumb/{id}", {
          pathParams: {
            id: props.navigationId,
          },
        })
        .catch(() => {
          console.error("Error while fetching breadcrumbs");
        }),
    ]);

    for (const response of responses) {
      if (response.status === "rejected") {
        console.error("[FrontendNavigationPage.vue]", response.reason.message);
      }
    }

    return {
      category: responses[0].status === "fulfilled" ? responses[0].value : null,
      breadcrumbs:
        responses[1].status === "fulfilled" ? responses[1].value : null,
    };
  },
  { server: true }
) as { data: Ref<any>, error: Ref<any> };

// Extract category data from the response
const categoryResponse = ref(data.value?.category);

// Function to refresh breadcrumbs from data (works with both cached and fresh data)
const refreshBreadcrumbs = () => {
  const responseData = data.value as any;
  if (responseData?.breadcrumbs) {
    const breadcrumbsData = responseData.breadcrumbs.data || responseData.breadcrumbs;
    if (breadcrumbsData) {
      buildDynamicBreadcrumbs(breadcrumbsData);
      navigationTrailStore.setTrail(
        (breadcrumbsData as Breadcrumb[]).map((breadcrumb) => ({
          ...breadcrumb,
          path: `/${breadcrumb.path}`,
        })),
        "category",
      );
    }
  }
};

// Refresh breadcrumbs immediately after useAsyncData resolves (works for cached data)
refreshBreadcrumbs();

// Watch navigationId to refresh when navigating to a different page
watch(
  () => props.navigationId,
  (newId, oldId) => {
    if (newId !== oldId && newId) {
      refreshBreadcrumbs();
    }
  }
);

// Use lifecycle hooks to ensure breadcrumbs refresh when component is mounted/activated
onMounted(() => {
  refreshBreadcrumbs();
});

onActivated(() => {
  refreshBreadcrumbs();
});

// If no category data, throw an error
if (!categoryResponse.value) {
  const statusMessage = error.value?.message || errors.value.join(", ") || "No category data received";
  console.error("[FrontendNavigationPage.vue] No category data:", statusMessage);
  throw createError({
    statusCode: 500,
    message: statusMessage,
  });
}

const { category } = useCategory(categoryResponse as Ref<Schemas["Category"]>);

const runtimeConfig = useRuntimeConfig();
useCmsHead(category, { mainShopTitle: "mysofabed.de" });
const { buildListItems } = useBreadcrumbJsonLD();

if (route?.path === "/") {
  useHead(() => {
    return {
      script: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            defu({
              "@context": "http://schema.org/",
              "@type": "WebPage",
              website: runtimeConfig.public.shopware?.devStorefrontUrl,
              organization: {
                "@type": "Organization",
                name: "MySofaBed",
                url: runtimeConfig.public.shopware?.devStorefrontUrl,
              },
              webpage: {
                "@type": "WebPage",
                url: runtimeConfig.public.shopware?.devStorefrontUrl + route.path,
              },
            }),
          ),
        },
      ],
    };
  });
} else {
  useHead(() => {
    const baseUrl = runtimeConfig.public.shopware?.devStorefrontUrl ?? "";
    const metaTitle =
      getTranslatedProperty(category.value, "metaTitle") ||
      getTranslatedProperty(category.value, "name");
    const metaDescription = getTranslatedProperty(
      category.value,
      "metaDescription",
    ).replace(/(<([^>]+)>)/gi, "");

    const scripts: { type: string; children: string }[] = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "http://schema.org/",
          "@type": "WebPage",
          name: metaTitle,
          description: metaDescription,
          url: `${baseUrl}${route.path}`,
        }),
      },
    ];

    if (category.value?.breadcrumb?.length) {
      const trail = category.value.breadcrumb.map((name) => {
        const trailItem = navigationTrailStore.trail.find((b) => b.name === name);
        return { name, path: trailItem?.path };
      });

      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: buildListItems(trail),
        }),
      });
    }

    return {
      script: scripts,
    };
  });
}
</script>

<template>
  <CmsPage v-if="category?.cmsPage" :content="category.cmsPage" />
</template>